// File: components/studio/Recorder.tsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { BlindModeOverlay } from '../BlindModeOverlay';
import { useStudioManager } from '../../context/hooks/useStudioManager';
import { useGuitarMatch } from '../../context/GuitarMatchContext';
import { RecordingResult, TimeSignature, tokensToStructuredGrid } from '../../types';
import { StudioConsole } from './Console';
import { WaveformMini } from './WaveformMini';
import { PingOverlay } from './PingOverlay';
import LogoGSCInline from '../LogoGSCInline';

// ✅ Local minimal type (évite l'import ActiveMission absent dans v28)
type MissionLike = { id?: string; name?: string } | null;

interface RecorderProps {
  mode: 'audio' | 'video' | 'screen';
  onRecordingComplete: (res: RecordingResult) => void;
  onCancel: () => void;
  lang?: 'en' | 'fr';
  activeMission?: MissionLike;
  bpm?: number;
  progression?: string[];
  autoStopAtBeat?: number;
  isTeacher?: boolean;
  referenceFile?: File | null;
  forceStudentView?: boolean;
}

/**
 * Normalise une progression pour que les pipes "|", "||", etc. soient des tokens séparés,
 * même si l'utilisateur tape "Am|F|C|G" sans espaces.
 */
function normalizeGridTokens(tokens: string[]): string[] {
  const out: string[] = [];
  for (const raw of tokens) {
    const s = String(raw ?? '').trim();
    if (!s) continue;

    // split while keeping pipes (|, ||, |||)
    const parts = s.split(/(\|+)/g).filter(Boolean);
    for (const p of parts) {
      const t = p.trim();
      if (!t) continue;
      out.push(t);
    }
  }
  return out;
}

const ChordHUD: React.FC<{
  progression: string[];
  isReady: boolean;
  status: string;
  currentBarIdx: number;
  selectedBarIdx: number | null;
  onSelectBar: (idx: number) => void;
}> = ({ progression, isReady, status, currentBarIdx, selectedBarIdx, onSelectBar }) => {
  const normalizedTokens = useMemo(() => normalizeGridTokens(progression), [progression]);
  const bars = useMemo(() => tokensToStructuredGrid(normalizedTokens), [normalizedTokens]);

  if (bars.length === 0) return null;

  const isVisible = isReady || status === 'COUNT_IN' || status === 'RECORDING';
  if (!isVisible) return null;

  // ✅ règle d’éclairage :
  // - en COUNT_IN/RECORDING : suit l’engine (currentBarIdx)
  // - en READY : suit la sélection utilisateur (selectedBarIdx)
  const activeIdx = status === 'READY' ? selectedBarIdx : currentBarIdx;

  return (
    <div
      className="
        absolute bottom-12 left-1/2 -translate-x-1/2
        w-full max-w-6xl px-8
        z-[2200]
        flex gap-4 overflow-x-auto no-scrollbar
        pb-4
        pointer-events-auto
      "
    >
      <div className="flex gap-4 mx-auto pointer-events-auto">
        {bars.map((chords, idx) => {
          const isActive = activeIdx !== null && idx === activeIdx;

          return (
            <button
              type="button"
              key={idx}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (status === 'READY') onSelectBar(idx);
              }}
              // ✅ clickable only in READY (sinon l’engine pilote)
              disabled={status !== 'READY'}
              className={`
                flex-1 min-w-[140px] p-6 rounded-[32px] border-2
                transition-all duration-500
                flex flex-col items-center justify-center gap-2
                backdrop-blur-md
                ${status !== 'READY' ? 'cursor-default' : 'cursor-pointer'}
                ${
                  isActive
                    ? 'bg-white border-white scale-110 shadow-[0_0_60px_rgba(255,255,255,0.4)] z-10'
                    : isReady
                      ? 'bg-black/40 border-white/20 opacity-90 hover:opacity-100 hover:border-white/40'
                      : 'bg-black/60 border-white/10 opacity-30'
                }
              `}
            >
              <span
                className={`text-[8px] font-black uppercase tracking-widest ${
                  isActive ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                Mesure {idx + 1}
              </span>

              <span
                className={`text-3xl font-black italic tracking-tighter whitespace-nowrap ${
                  isActive ? 'text-black' : 'text-white'
                }`}
              >
                {chords.join(' ')}
              </span>

              {status === 'READY' && (
                <span className="mt-1 text-[9px] text-slate-500 italic">
                  {isActive ? 'Sélectionnée' : 'Cliquer'}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const Recorder: React.FC<RecorderProps> = (props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const masterVideoRef = useRef<HTMLVideoElement>(null);

  const {
    targetBpm,
    setTargetBpm,
    timeSignature,
    setTimeSignature,
    globalCalibrationOffset,
    setGlobalCalibrationOffset,
    lang: contextLang,
    manualGrid,
    hasDoneGlobalCalibration,
    setHasDoneGlobalCalibration,
  } = useGuitarMatch();

  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape');
  const [viewMode, setViewMode] = useState<'student' | 'split' | 'teacher'>(
    props.forceStudentView ? 'student' : 'split'
  );
  const [isMirrored, setIsMirrored] = useState(true);

  // Volume master piloté par la console
  const [teacherVolume, setTeacherVolume] = useState(0.5);

  const currentLang = props.lang || contextLang || 'fr';

  const displayGrid = useMemo(() => {
    if (props.progression && props.progression.length > 0) return props.progression;

    // manualGrid peut contenir des "|" avec ou sans espaces => split simple,
    // ChordHUD re-normalise ensuite (couvre "Am|F|C|G").
    return manualGrid.split(/\s+/).filter((t) => t.length > 0);
  }, [props.progression, manualGrid]);

  const masterFileUrl = useMemo(() => {
    if (props.referenceFile) return URL.createObjectURL(props.referenceFile);
    return null;
  }, [props.referenceFile]);

  const computedAutoStop = useMemo(() => {
    if (props.autoStopAtBeat) return props.autoStopAtBeat;

    const normalized = normalizeGridTokens(displayGrid);
    const bars = tokensToStructuredGrid(normalized);
    const beatsPerBar = parseInt(String(timeSignature).split('/')[0]) || 4;
    return bars.length > 0 ? bars.length * beatsPerBar : undefined;
  }, [displayGrid, timeSignature, props.autoStopAtBeat]);

  // ✅ Règle (1) + anti faux-positif
  const handleCalibrationComplete = (offset: number) => {
    setGlobalCalibrationOffset(offset);

    const isFiniteNumber = typeof offset === 'number' && Number.isFinite(offset);
    const isNonZero = isFiniteNumber && Math.abs(offset) > 1e-6;

    if (isNonZero) setHasDoneGlobalCalibration(true);
    else setHasDoneGlobalCalibration(false);
  };

  const studio = useStudioManager({
    mode: props.mode,
    bpm: props.bpm || targetBpm,
    timeSignature: timeSignature as TimeSignature,
    calibrationOffset: globalCalibrationOffset,
    orientation,
    onCalibrationComplete: handleCalibrationComplete,
    onRecordingComplete: props.onRecordingComplete,
    autoStopAtBeat: computedAutoStop,
    masterVideoRef: masterVideoRef,
  });

  const [isBlindActive, setIsBlindActive] = useState(false);
  const [isConsoleCollapsed, setIsConsoleCollapsed] = useState(false);

  // ✅ NEW: sélection utilisateur de la mesure (READY)
  const [selectedBarIdx, setSelectedBarIdx] = useState<number | null>(null);

  // ✅ helper pour “brancher” l’anatomie spectrale ailleurs
  const emitBarSelect = useCallback((barIdx: number) => {
    try {
      sessionStorage.setItem('gsc_selected_bar_idx', String(barIdx));
      window.dispatchEvent(new CustomEvent('gsc:bar-select', { detail: { barIdx } }));
    } catch {
      // no-op
    }
  }, []);

  useEffect(() => {
    if (videoRef.current && studio.previewStream) {
      videoRef.current.srcObject = studio.previewStream;
      videoRef.current.play().catch(() => {});
    }
  }, [studio.previewStream]);

  const isPreviewPhase = studio.status === 'PREVIEW' || studio.status === 'PROCESSING';

  // Appliquer teacherVolume au player master
  useEffect(() => {
    const v = masterVideoRef.current;
    if (!v) return;
    v.volume = Math.max(0, Math.min(1, teacherVolume));
  }, [teacherVolume]);

  useEffect(() => {
    if (studio.status === 'RECORDING' && props.activeMission?.id === 'blind_test') {
      setIsBlindActive(true);
    } else {
      setIsBlindActive(false);
    }
  }, [studio.status, props.activeMission]);

  // ✅ Quand on passe en READY, on initialise la sélection sur la mesure 0 si rien n’est sélectionné
  useEffect(() => {
    if (studio.status === 'READY') {
      setSelectedBarIdx((prev) => {
        const next = prev ?? 0;
        emitBarSelect(next);
        return next;
      });
    }
    // Pendant COUNT_IN/RECORDING, on ne force pas la sélection (c’est l’engine qui pilote)
  }, [studio.status, emitBarSelect]);

  const handleDownload = () => {
    if (!studio.reviewUrl) return;
    const currentBpm = props.bpm || targetBpm;
    const fileName = `GSC_Take_${currentBpm}.webm`;
    const a = document.createElement('a');
    a.href = studio.reviewUrl;
    a.download = fileName;
    a.click();
  };

  // Mur calibration
  const isBlockingRequired =
    !hasDoneGlobalCalibration && (studio.status === 'READY' || studio.status === 'BOOTING');

  return (
    <div className="fixed inset-0 bg-black flex flex-row z-[500] font-sans overflow-hidden">
      <BlindModeOverlay active={isBlindActive} />

      <StudioConsole
        lang={currentLang}
        bpm={props.bpm || targetBpm}
        setBpm={setTargetBpm}
        timeSignature={timeSignature as TimeSignature}
        setTimeSignature={setTimeSignature}
        micGain={1.0}
        setMicGain={() => {}}
        metronomeVolume={0.5}
        setMetronomeVolume={() => {}}
        teacherVolume={teacherVolume}
        setTeacherVolume={setTeacherVolume}
        vuLevel={studio.vuLevel}
        selectedDeviceId={studio.selectedAudioId}
        audioDevices={studio.audioDevices}
        onDeviceChange={studio.setSelectedAudioId}
        selectedVideoId={studio.selectedVideoId}
        videoDevices={studio.videoDevices}
        onVideoDeviceChange={studio.setSelectedVideoId}
        offset={globalCalibrationOffset}
        setOffset={setGlobalCalibrationOffset}
        isVisible={true}
        onClose={props.onCancel}
        isCollapsed={isConsoleCollapsed}
        onToggleCollapse={() => setIsConsoleCollapsed(!isConsoleCollapsed)}
        onRecalibrate={studio.startCalibration}
        isLockedBySystem={
          studio.status === 'RECORDING' ||
          studio.status === 'COUNT_IN' ||
          studio.status === 'CALIBRATING'
        }
      />

      <main className="flex-1 relative bg-[#010204] flex flex-col items-center justify-center overflow-hidden">
        <div
          className={`absolute inset-0 flex flex-col transition-all duration-1000 ${
            isBlockingRequired ? 'blur-[50px] grayscale brightness-50' : ''
          }`}
        >
          {masterFileUrl && (viewMode === 'split' || viewMode === 'teacher') && (
            <div className="flex-1 relative bg-slate-900 border-b border-white/10 overflow-hidden flex items-center justify-center">
              <video
                ref={masterVideoRef}
                src={masterFileUrl}
                playsInline
                muted={false}
                className={`h-full w-full object-cover opacity-90 contrast-125 transition-opacity duration-500 ${
                  isPreviewPhase ? 'opacity-30' : 'opacity-90'
                }`}
              />
            </div>
          )}

          {(viewMode === 'split' || viewMode === 'student') && !isPreviewPhase && (
            <div className="flex-1 relative bg-black overflow-hidden flex items-center justify-center">
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className={`absolute inset-0 h-full w-full object-cover transition-all duration-700 grayscale-[0.2] ${
                  isMirrored ? 'scale-x-[-1]' : 'scale-x-1'
                } ${orientation === 'portrait' ? 'opacity-40 blur-md' : 'opacity-80'}`}
              />
            </div>
          )}
        </div>

        <div className="absolute top-[60px] left-[60px] z-30 pointer-events-none opacity-50 select-none">
          <LogoGSCInline size={orientation === 'portrait' ? 80 : 118} className="text-white" />
        </div>

        <div className="absolute top-12 left-1/2 -translate-x-1/2 z-20">
          <WaveformMini
            analyser={studio.analyser}
            isActive={studio.status === 'READY' || studio.status === 'RECORDING'}
            bpm={props.bpm || targetBpm}
          />
        </div>

        {studio.status === 'READY' && !isBlockingRequired && (
          <div className="absolute top-12 right-12 z-50 flex flex-col gap-4 items-end">
            <div className="flex gap-2 p-1.5 bg-black/60 border border-white/10 rounded-2xl backdrop-blur-3xl shadow-2xl">
              <button
                onClick={() => setIsMirrored(!isMirrored)}
                className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${
                  isMirrored ? 'bg-cyan-500 text-black' : 'text-slate-400'
                }`}
              >
                Mirror
              </button>
              <div className="w-px h-6 bg-white/10 my-auto"></div>
              <button
                onClick={() => setOrientation('landscape')}
                className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${
                  orientation === 'landscape' ? 'bg-white text-black' : 'text-slate-400'
                }`}
              >
                Wide
              </button>
              <button
                onClick={() => setOrientation('portrait')}
                className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${
                  orientation === 'portrait' ? 'bg-white text-black' : 'text-slate-400'
                }`}
              >
                9:16
              </button>
            </div>
          </div>
        )}

        <PingOverlay
          active={studio.status === 'CALIBRATING'}
          beat={studio.displayBeat}
          status={studio.status}
          latency={studio.calibratedLatency}
          error={studio.errorTypeOriginal}
          hardwareType={studio.hardwareType}
          onForceApply={studio.retry}
        />

        {/* ✅ CLICKABLE HUD + READY selection */}
        <ChordHUD
          progression={displayGrid}
          currentBarIdx={studio.currentBarIdx}
          selectedBarIdx={selectedBarIdx}
          onSelectBar={(idx) => {
            setSelectedBarIdx(idx);
            emitBarSelect(idx);
          }}
          isReady={studio.status === 'READY'}
          status={studio.status}
        />

        <div className="absolute inset-0 z-[1500] flex flex-col items-center justify-center pointer-events-none">
          {(studio.status === 'READY' || studio.status === 'BOOTING') && (
            <div className="flex flex-col items-center gap-10 pointer-events-auto animate-fade-in w-full max-w-2xl px-6">
              {isBlockingRequired ? (
                <div className="flex flex-col items-center gap-10 bg-black/95 p-12 md:p-16 rounded-[64px] border-4 border-cyan-500 shadow-[0_0_120px_rgba(6,182,212,0.5)] text-center animate-portal-reveal backdrop-blur-3xl">
                  <div className="w-24 h-24 rounded-full bg-cyan-500/10 border-4 border-cyan-500 flex items-center justify-center text-5xl animate-pulse shadow-[0_0_30px_rgba(6,182,212,0.3)]">
                    🛰️
                  </div>
                  <div className="space-y-6">
                    <h3 className="text-[12px] font-black text-cyan-400 uppercase tracking-[0.5em] italic">
                      ÉCHÉANCE MATÉRIELLE
                    </h3>
                    <h2 className="text-4xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-tight">
                      Calibration <br />
                      Impérative
                    </h2>
                    <p className="text-xs text-slate-400 font-medium italic leading-relaxed max-w-sm mx-auto opacity-80">
                      "Un score rythmique n'a aucune valeur sans calibration préalable du microphone. Ce test dure 3 secondes."
                    </p>
                  </div>
                  <button
                    onClick={studio.startCalibration}
                    className="w-full py-7 bg-white text-black rounded-full font-black uppercase text-xs tracking-[0.4em] hover:bg-cyan-400 hover:scale-[1.03] transition-all shadow-[0_15px_40px_rgba(0,0,0,0.5)] active:scale-95"
                  >
                    DÉMARRER LE PING MAINTENANT 🛰️
                  </button>
                </div>
              ) : (
                <button
                  onClick={studio.startRecording}
                  id="studio-rec-btn"
                  className="w-44 h-44 md:w-60 md:h-60 rounded-full bg-red-600 hover:bg-red-500 text-white font-black uppercase text-3xl shadow-[0_0_80px_rgba(220,38,38,0.5)] transition-all hover:scale-110 active:scale-90 flex flex-col items-center justify-center gap-4 group"
                >
                  <div className="w-8 h-8 bg-white rounded-full animate-pulse group-hover:scale-125 transition-transform"></div>
                  <span>REC</span>
                </button>
              )}
            </div>
          )}

          {studio.status === 'COUNT_IN' && (
            <div className="text-[200px] font-black italic text-white animate-ping drop-shadow-[0_0_60px_rgba(255,255,255,0.4)]">
              {studio.displayBeat}
            </div>
          )}

          {studio.status === 'RECORDING' && (
            <div className="flex flex-col items-center gap-10 pointer-events-auto">
              <div className="flex items-center gap-6 px-12 py-6 bg-red-600 rounded-full shadow-[0_0_80px_rgba(220,38,38,0.4)] animate-pulse border-2 border-white/20">
                <div className="w-4 h-4 bg-white rounded-full"></div>
                <span className="text-4xl font-black text-white italic uppercase tracking-tighter">
                  Recording Live
                </span>
              </div>
              <button
                onClick={studio.stopRecording}
                className="px-16 py-6 bg-white text-black rounded-full font-black uppercase text-xs tracking-[0.3em] hover:bg-red-600 hover:text-white transition-all shadow-3xl"
              >
                STOP MATCH
              </button>
            </div>
          )}

          {studio.status === 'PREVIEW' && studio.reviewUrl && (
            <div className="fixed inset-0 z-[3000] bg-black/98 backdrop-blur-3xl flex flex-col items-center justify-center gap-10 p-6 md:p-10 animate-fade-in overflow-y-auto pointer-events-auto">
              <div
                className={`w-full relative flex items-center justify-center ${
                  orientation === 'portrait' ? 'max-w-[420px] h-[75vh]' : 'max-w-5xl aspect-video'
                }`}
              >
                <div className="w-full h-full bg-black rounded-[48px] md:rounded-[64px] overflow-hidden border-2 border-white/10 shadow-3xl relative">
                  <video src={studio.reviewUrl} controls autoPlay playsInline className="w-full h-full object-cover" />
                </div>
              </div>

              <div className="flex flex-wrap gap-4 w-full max-w-4xl justify-center">
                <button
                  onClick={studio.reboot}
                  className="flex-1 min-w-[140px] py-6 bg-white/5 border border-white/10 text-slate-400 rounded-[28px] font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all active:scale-95"
                >
                  REFAIRE
                </button>
                <button
                  onClick={handleDownload}
                  className="flex-1 min-w-[140px] py-6 bg-white/5 border border-white/10 text-white rounded-[28px] font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all active:scale-95"
                >
                  SAUVEGARDER 📥
                </button>
                <button
                  onClick={studio.validateTake}
                  className="flex-[2] min-w-[200px] py-6 bg-smart-accent text-white rounded-[28px] font-black uppercase text-sm tracking-[0.3em] shadow-2xl hover:scale-105 transition-all active:scale-95"
                >
                  CONFIRMER MATCH! 🎸
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
